import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  Sun,
  Moon,
  Monitor
} from 'lucide-react';
import { FaFacebook as Facebook } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSettingsStore } from '@/stores/useSettingsStore';
import { Logo } from '@/components/ui/Logo';
import { isValidEmail } from '@/utils/emailValidation';
import { fetchFooterStates } from '@/api/displaysOverview';
import { stateFromSlug, stateSlug } from '@/lib/usStates';
import { buildStateDisplayPath } from '@/routing/discoveryPaths';
import { FACEBOOK_GROUP_URL } from '@/lib/voiceRewards';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { fetchHasLiveDeals } from '@/lib/promotions/dealsClient';
import { ReportProblemButton } from '@/components/feedback/ReportProblemButton';

// Renders only where the browser has decided the app is installable and
// offers its native install dialog on click.
function InstallAppButton() {
  const { canInstall, promptInstall } = useInstallPrompt();
  if (!canInstall) return null;
  return (
    <button
      type="button"
      onClick={() => void promptInstall()}
      className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors"
    >
      Install the app
    </button>
  );
}

// Fallback list shown while (or if) the distinct-states query fails or
// returns nothing — the footer must always render useful location links.
const FOOTER_FALLBACK_STATES = ['TX', 'CA', 'FL', 'NY', 'PA', 'OH', 'NC', 'GA'];

// Distinct states change rarely; fetch once per session and reuse the same
// promise for every footer mount instead of re-querying on each page view.
let footerStatesPromise: Promise<string[]> | null = null;

const loadFooterStates = (): Promise<string[]> => {
  if (!footerStatesPromise) {
    footerStatesPromise = (async () => {
      // The server pages through every active display and merges state
      // spellings; the browser cannot, PostgREST caps one response at 1,000 rows.
      const states = await fetchFooterStates();
      const topStates = (states ?? []).filter((state) => stateSlug(state) !== null);

      return topStates.length > 0 ? topStates : FOOTER_FALLBACK_STATES;
    })().catch(() => FOOTER_FALLBACK_STATES);
  }
  return footerStatesPromise;
};

/** Resolve any raw DB state value ("TX", "Texas", "TX - Texas") to its full display name. */
const footerStateLabel = (stateValue: string): string =>
  stateFromSlug(stateSlug(stateValue) ?? '')?.name ?? stateValue;

type SubscribeState = 'idle' | 'submitting' | 'success' | 'error';

const Footer = () => {
  const { settings, setTheme } = useSettingsStore();
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribeState, setSubscribeState] = useState<SubscribeState>('idle');
  const [subscribeError, setSubscribeError] = useState('');
  // Render the fallback immediately and swap in the queried states once; the
  // footer must fail silently and never break the page it lives on.
  const [hasDeals, setHasDeals] = useState(false);
  const [locationStates, setLocationStates] = useState<string[]>(FOOTER_FALLBACK_STATES);

  useEffect(() => {
    let live = true;
    void fetchHasLiveDeals().then((v) => { if (live) setHasDeals(v); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadFooterStates()
      .then((states) => {
        if (!cancelled) setLocationStates(states);
      })
      .catch(() => {
        // Already rendering the fallback list; nothing to recover.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleTheme = () => {
    // Cycle through all three theme options: dark → light → system → dark
    switch (settings.theme) {
      case 'dark':
        setTheme('light');
        break;
      case 'light':
        setTheme('system');
        break;
      case 'system':
        setTheme('dark');
        break;
      default:
        setTheme('dark');
    }
  };

  const handleSubscribe = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (subscribeState === 'submitting') return;

    if (!isValidEmail(subscribeEmail)) {
      setSubscribeState('error');
      setSubscribeError('Please enter a valid email address.');
      return;
    }

    setSubscribeState('submitting');
    setSubscribeError('');

    let serverError: string | undefined;

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: subscribeEmail.trim(), source: 'footer' }),
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok) {
        setSubscribeState('success');
        return;
      }

      serverError = result.error;
    } catch {
      // Network failure — fall through to the generic message below.
    }

    setSubscribeState('error');
    // Only ever show a server-supplied message or the generic fallback —
    // never raw browser error text like "Failed to fetch".
    setSubscribeError(serverError || 'Something went wrong. Please try again later.');
  };

  return (
    <footer className="bg-card border-t border-border">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Column 1 - About */}
          <div className="space-y-4">
            <Link to="/" viewTransition className="text-2xl font-bold flex items-center space-x-2">
              <Logo />
            </Link>
            <p className="text-muted-foreground">
              Connecting holiday light enthusiasts with amazing displays and synchronized sequences.
            </p>
            <a
              href={FACEBOOK_GROUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-3 rounded-lg bg-muted p-3 hover:bg-muted/80 transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
            >
              <Facebook size={20} aria-hidden="true" className="mt-0.5 shrink-0 text-[#1877F2]" />
              <span>
                <span className="block text-sm font-medium group-hover:underline underline-offset-4">
                  Join our Facebook group
                </span>
                <span className="block text-sm text-muted-foreground">
                  Member offers like free voiceovers, plus show updates.
                </span>
              </span>
            </a>
            <InstallAppButton />
          </div>

          {/* Column 2 - Quick Links */}
          <div>
            <h3 className="font-medium text-lg mb-4">Quick links</h3>
            <nav className="flex flex-col space-y-2">
              <FooterLink href="/displays" label="Displays" />
              <FooterLink href="/displays/map" label="Map" />
              <FooterLink href="/sequences" label="Sequences" />
              <FooterLink href="/categories" label="Categories" />
              <FooterLink href="/directory" label="Sequence directory" />
              <FooterLink href="/videos" label="Videos" />
              {hasDeals && <FooterLink href="/deals" label="Sales" />}
              <FooterLink href="/bounties" label="Bounties" />
              <FooterLink href="/about" label="About" />
              <FooterLink href="/contact" label="Contact" />
            </nav>
          </div>

          {/* Column 3 - Browse by Location */}
          <div>
            <h3 className="font-medium text-lg mb-4">Browse by location</h3>
            <nav className="flex flex-col space-y-2">
              <FooterLink href="/displays" label="All displays" />
              {locationStates.map((stateValue) => (
                <FooterLink
                  key={stateValue}
                  href={buildStateDisplayPath(stateValue)}
                  label={footerStateLabel(stateValue)}
                />
              ))}
            </nav>
          </div>

          {/* Column 4 - Resources */}
          <div>
            <h3 className="font-medium text-lg mb-4">Resources</h3>
            <nav className="flex flex-col space-y-2">
              <FooterLink href="/help" label="Help center" />
              <FooterLink href="/developers" label="Developer docs" />
              <FooterLink href="/ai" label="Use with ChatGPT or Claude" />
              <FooterLink href="/for-owners" label="For display owners" />
              <FooterLink href="/for-owners/first-show" label="Your first light show" />
              <FooterLink href="/sell" label="Sell your sequences" />
              <FooterLink href="/voices" label="Free voiceovers" />
              <FooterLink href="/installers" label="Light Hunt Studio for installers" />
              <FooterLink href="/roadmap" label="Roadmap" />
              <FooterLink href="/feedback" label="Feedback" />
              <ReportProblemButton />
              <FooterLink href="/terms" label="Terms of service" />
              <FooterLink href="/seller-terms" label="Seller terms" />
              <FooterLink href="/privacy" label="Privacy policy" />
              <FooterLink href="/refunds" label="Refund policy" />
              <FooterLink href="/dmca" label="DMCA / copyright" />
              <FooterLink href="/privacy#cookies" label="Cookie choices" />
            </nav>
          </div>

          {/* Column 5 - Newsletter */}
          <div>
            <h3 id="footer-newsletter-title" className="font-medium text-lg mb-4">
              Stay Updated
            </h3>
            {subscribeState === 'success' ? (
              <div role="status" className="space-y-2">
                <p className="text-sm font-medium">You're on the list.</p>
                <p className="text-sm text-muted-foreground">
                  We'll email you occasional updates about new displays and sequences.
                </p>
              </div>
            ) : (
              <>
                <p className="text-muted-foreground mb-4">
                  Subscribe to our newsletter for the latest displays and sequences.
                </p>
                <form
                  onSubmit={handleSubscribe}
                  noValidate
                  className="flex flex-col space-y-2"
                  aria-labelledby="footer-newsletter-title"
                  aria-busy={subscribeState === 'submitting'}
                  aria-describedby={subscribeState === 'error' ? 'footer-newsletter-error' : undefined}
                >
                  <Input
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="Enter your email"
                    aria-label="Email address"
                    className="rounded-lg"
                    value={subscribeEmail}
                    onChange={(event) => setSubscribeEmail(event.target.value)}
                    disabled={subscribeState === 'submitting'}
                  />
                  <Button
                    type="submit"
                    className="w-full rounded-lg"
                    disabled={subscribeState === 'submitting'}
                  >
                    {subscribeState === 'submitting' ? 'Subscribing…' : 'Subscribe'}
                  </Button>
                  {subscribeState === 'submitting' && (
                    <p
                      role="status"
                      aria-live="polite"
                      aria-label="Newsletter status"
                      className="text-sm text-muted-foreground"
                    >
                      Subscribing…
                    </p>
                  )}
                  {subscribeState === 'error' && subscribeError && (
                    <p
                      id="footer-newsletter-error"
                      role="alert"
                      className="text-sm text-destructive"
                    >
                      {subscribeError}
                    </p>
                  )}
                </form>
              </>
            )}
          </div>
        </div>

        <div className="border-t border-border mt-10 pt-6 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Light Hunt. All rights reserved.
          </p>
          <div className="flex items-center space-x-4 mt-4 md:mt-0">
            <Link to="/terms" viewTransition className="text-sm text-muted-foreground hover:text-foreground">
              Terms
            </Link>
            <Link to="/privacy" viewTransition className="text-sm text-muted-foreground hover:text-foreground">
              Privacy
            </Link>
            <Link to="/privacy#cookies" viewTransition className="text-sm text-muted-foreground hover:text-foreground">
              Cookies
            </Link>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2"
              aria-label={`Current: ${settings.theme} theme. Click to switch to ${
                settings.theme === 'dark' ? 'light' : 
                settings.theme === 'light' ? 'system' : 'dark'
              } theme`}
              title={`Current: ${settings.theme} theme. Click to switch to ${
                settings.theme === 'dark' ? 'light' : 
                settings.theme === 'light' ? 'system' : 'dark'
              } theme`}
            >
              {settings.theme === 'dark' ? (
                <Moon size={16} className="text-muted-foreground hover:text-foreground transition-colors" />
              ) : settings.theme === 'light' ? (
                <Sun size={16} className="text-muted-foreground hover:text-foreground transition-colors" />
              ) : (
                <Monitor size={16} className="text-muted-foreground hover:text-foreground transition-colors" />
              )}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

interface FooterLinkProps {
  href: string;
  label: string;
}

const FooterLink: React.FC<FooterLinkProps> = ({ href, label }) => {
  return (
    <Link
      to={href}
      viewTransition
      className="text-muted-foreground hover:text-foreground transition-colors"
    >
      {label}
    </Link>
  );
};

export default Footer;
